import { useEffect, useLayoutEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { MenuItemDataType, MenuItemTarget } from 'application/providers/MenuItemsProvider/types';
import { RouterUtils, useNavigate } from 'application/providers/RouterProvider';
import PointsPage, { POINTS_PAGE_PATH, IdParamValues } from 'application/pages/PointsPage';
import VideoStreamsPage, {
  VIDEO_STREAMS_PAGE_PATH,
  VideoStreamsPageParamsValues,
} from 'application/pages/VideoStreamsPage';
import PricePlanPage, { PRICE_PLAN_PAGE_PATH } from 'application/pages/PricePlanPage';
import StreamGroupsPage, { StreamsCategoryParamValues, STREAMS_CATEGORY_PATH } from 'application/pages/StreamPage';
import { useInjection, UsePointRepositoryType, KEYS } from 'application/providers/DIContainerProvider';
import CompanyPage, { COMPANY_PAGE_PATH } from 'application/pages/CompanyPage';
import BillingPage, { BILLING_PAGE_PATH, StateParamValues } from 'application/pages/BillingPage';
import { useMenuItemsContext } from 'application/providers/MenuItemsProvider';
import AdsPage, { ADS_PAGE_PATH_ALIAS } from 'application/pages/AdsPage';
import ReportsPage, { REPORTS_PAGE_PATH } from 'application/pages/ReportsPage';
import BusinessProfilePage, {
  BUSINESS_PROFILE_PAGE_PATH,
  BusinessProfilePageIdParamValues,
} from 'application/pages/BusinessProfilePage';
import ShelfPage, { SHELF_PAGE_PATH } from 'application/pages/ShelfPage';
import CompanyUtils from 'application/utils/CompanyUtils';
import AnalyticsService, { EventNameAMP } from 'application/services/AnalyticsService';
import { LOGOUT_PAGE_PATH } from 'application/consts';
import { ModalTypes, useModalContext } from 'application/providers/ModalProvider';
import { useAppSelector } from 'application/providers/StoreProvider';
import {
  selectIsCertLetterAvailable,
  selectIsAuthorized,
  selectIsFeatureShelfPage,
  selectIsShowReports,
  selectIsVideoAvailable,
} from 'application/store';

import IconMapPin from '@zvuk-b2b/react-uikit/ui/icons/MapPin';
import IconMusic from '@zvuk-b2b/react-uikit/ui/icons/Music';
import IconCreditCard from '@zvuk-b2b/react-uikit/ui/icons/CreditCard';
import IconUser from '@zvuk-b2b/react-uikit/ui/icons/User';
import IconClipboardList from '@zvuk-b2b/react-uikit/ui/icons/ClipboardList';
import FileText from '@zvuk-b2b/react-uikit/ui/icons/FileText';
import IconAdvertising from '@zvuk-b2b/react-uikit/ui/icons/Advertising';
import IconSettings from '@zvuk-b2b/react-uikit/ui/icons/Settings';
import IconBriesCase from '@zvuk-b2b/react-uikit/ui/icons/Briefcase';
import IconGrid from '@zvuk-b2b/react-uikit/ui/icons/Grid';
import IconPlayCircle from '@zvuk-b2b/react-uikit/ui/icons/PlayCircle';

import useCurrentPricePlan from '@entities/pricePlan/api/useCurrentPricePlan';
import {
  AuthCurrentUserDocument,
  AuthCurrentUserQuery,
  CompanyContractKind,
  CompanyContractStatusEnum,
  Country,
} from '@shared/api/graphql/generated';
import { FetchPolicyKeys } from '@shared/api/graphql/types';

import { usePointsGroupsErrors } from 'application/hooks/usePointsGroupsErrors';
import { TEXTS } from './texts';

const useMainMenuItems = () => {
  const isAuthorized = useAppSelector(selectIsAuthorized);
  const isFeatureShelfPage = useAppSelector(selectIsFeatureShelfPage);
  const isCertLetterAvailable = useAppSelector(selectIsCertLetterAvailable);
  const isShowReports = useAppSelector(selectIsShowReports);
  const isVideoAvailable = useAppSelector(selectIsVideoAvailable);

  const { data } = useQuery<AuthCurrentUserQuery>(AuthCurrentUserDocument, {
    fetchPolicy: FetchPolicyKeys.CACHE_FIRST,
    skip: !isAuthorized,
  });

  const currentUser = data?.currentUserPureQuery;

  const userEmail = currentUser?.email;
  const userPhone = currentUser?.phone;
  const userFirstName = currentUser?.firstName;

  const currentCompany = currentUser?.mainCompany;

  const companyId = currentCompany?.id;
  const companyPricePlanAlias = currentCompany?.pricePlan?.alias;
  const companyCountry = currentCompany?.country;
  const companyRequisite = currentCompany?.requisite;
  const companyIsBillingEnabled = currentCompany?.isBillingEnabled;

  const actualContractType = currentCompany?.contracts?.find(
    (c: { status: CompanyContractStatusEnum }) => c.status === CompanyContractStatusEnum.Actual
  )?.typeContract;

  const { data: currentPricePlan, request } = useCurrentPricePlan();

  const { showCertModal } = useMenuItemsContext();
  const navigate = useNavigate();
  const { openModal, closeModal } = useModalContext();

  const usePointRepository = useInjection<UsePointRepositoryType>(KEYS.POINT_REPOSITORY);
  const { clearCurrentPoint } = usePointRepository();

  const offerLink = currentPricePlan.pricePlan?.pricePlan?.offerLink;

  useEffect(() => {
    if (currentCompany && companyPricePlanAlias !== currentPricePlan.pricePlan?.pricePlan?.alias && companyId) {
      request(companyId);
    }
  }, [companyPricePlanAlias, currentPricePlan.pricePlan?.pricePlan?.alias]);

  const onChangePasswordClick = () => {
    AnalyticsService.event(EventNameAMP.USER_PASSWORD_CHANGE_CLICKED);

    openModal(ModalTypes.PROFILE_CHANGE_PASSWORD, {});
  };

  const onLogoutClick = () => {
    closeModal();
    navigate(LOGOUT_PAGE_PATH);
  };

  const showProfile = () => {
    openModal(ModalTypes.PROFILE_INFO, {
      onChangePasswordClick,
      onLogoutClick,
    });
  };

  const hasPointsGroupsErrors = usePointsGroupsErrors();

  return useMemo<MenuItemDataType[]>(() => {
    const isCertificateMenuItemVisible =
      isCertLetterAvailable &&
      companyCountry &&
      companyRequisite &&
      !!CompanyUtils.getCompanyIndividualNumberByCountry(companyCountry, companyRequisite);

    const items = [
      {
        pathname: RouterUtils.generatePath(STREAMS_CATEGORY_PATH, {
          category: StreamsCategoryParamValues.RECOMMENDED,
        }),
        state: { search: 'reset' },
        title: TEXTS.MENU_TITLE_STREAMS,
        icon: IconMusic,
        target: MenuItemTarget.DEFAULT,
        config: StreamGroupsPage.config,
        onClick: clearCurrentPoint,
        isVisible: true,
      },
      {
        pathname: RouterUtils.generatePath(VIDEO_STREAMS_PAGE_PATH, {
          id: VideoStreamsPageParamsValues.ALL,
        }),
        state: { search: 'reset' },
        title: TEXTS.MENU_TITLE_VIDEO_STREAMS,
        icon: IconPlayCircle,
        target: MenuItemTarget.DEFAULT,
        config: VideoStreamsPage.config,
        onClick: clearCurrentPoint,
        isVisible: isVideoAvailable,
      },
      {
        pathname: RouterUtils.generatePath(POINTS_PAGE_PATH, { id: IdParamValues.ALL }),
        title: TEXTS.MENU_TITLE_POINTS,
        icon: IconMapPin,
        target: MenuItemTarget.DEFAULT,
        config: PointsPage.config,
        onClick: clearCurrentPoint,
        isVisible: true,
        withError: hasPointsGroupsErrors,
      },
      {
        pathname: ADS_PAGE_PATH_ALIAS,
        title: TEXTS.MENU_TITLE_AD,
        icon: IconAdvertising,
        target: MenuItemTarget.DEFAULT,
        config: AdsPage.config,
        isVisible: true,
      },
      {
        pathname: REPORTS_PAGE_PATH,
        title: TEXTS.MENU_TITLE_REPORTS,
        icon: IconClipboardList,
        target: MenuItemTarget.DEFAULT,
        config: ReportsPage.config,
        isVisible: isShowReports,
      },
      {
        pathname: RouterUtils.generatePath(BUSINESS_PROFILE_PAGE_PATH, { id: BusinessProfilePageIdParamValues.ALL }),
        title: TEXTS.MENU_TITLE_BUSINESS_PROFILE,
        icon: IconBriesCase,
        target: MenuItemTarget.DEFAULT,
        config: BusinessProfilePage.config,
        isVisible: true,
      },
      {
        pathname: SHELF_PAGE_PATH,
        title: TEXTS.MENU_TITLE_SHELF,
        icon: IconGrid,
        target: MenuItemTarget.DEFAULT,
        config: ShelfPage.config,
        isVisible: isFeatureShelfPage && companyCountry === Country.Ru,
      },
      {
        pathname: RouterUtils.generatePath(BILLING_PAGE_PATH, { state: StateParamValues.INFO }),
        title: TEXTS.MENU_TITLE_BILLING,
        icon: IconCreditCard,
        target: MenuItemTarget.BOTTOM,
        config: BillingPage.config,
        onClick: clearCurrentPoint,
        isVisible: companyIsBillingEnabled === true && actualContractType === CompanyContractKind.Offerta,
      },
      {
        pathname: RouterUtils.generatePath(PRICE_PLAN_PAGE_PATH),
        title: TEXTS.MENU_TITLE_PRICE_PLAN,
        icon: IconCreditCard,
        target: MenuItemTarget.BOTTOM,
        config: PricePlanPage.config,
        isVisible: companyIsBillingEnabled === false,
      },
      {
        title: TEXTS.MENU_TITLE_SETTINGS,
        icon: IconSettings,
        target: MenuItemTarget.BOTTOM,
        pathname: COMPANY_PAGE_PATH,
        config: CompanyPage.config,
        isVisible: true,
      },
      {
        title: TEXTS.MENU_TITLE_SHOW_CERTIFICATE,
        icon: FileText,
        target: MenuItemTarget.BOTTOM,
        onClick: () => {
          showCertModal();
        },
        isVisible: Boolean(isCertificateMenuItemVisible),
      },
      {
        title: userEmail || userPhone || userFirstName,
        icon: IconUser,
        target: MenuItemTarget.PROFILE,
        onClick: () => showProfile(),
        isVisible: true,
      },
    ];

    return items;
  }, [
    userEmail,
    userPhone,
    userFirstName,
    companyCountry,
    companyRequisite,
    companyIsBillingEnabled,
    actualContractType,
    offerLink,
    isShowReports,
    isFeatureShelfPage,
    isCertLetterAvailable,
    isVideoAvailable,
    hasPointsGroupsErrors,
  ]);
};

const useMenusPathnames = () => {
  const { mainMenu } = useMenuItemsContext();

  const mainMenuItems = useMainMenuItems();

  useLayoutEffect(() => mainMenu.setItems(mainMenuItems), [mainMenuItems]);
};

export default useMenusPathnames;
